import React, { useCallback, useEffect } from 'react';
import { css, SerializedStyles } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { breakpoints } from '@frontend/responsiveness';
import { validateUUIDV4 } from '@frontend/string';
import { pendo } from '@frontend/tracking';
import { theme } from '@frontend/theme';
import { Button, SearchField, Text, useControlledField, useDebouncedValue } from '@frontend/design-system';
import { trackingIds } from '../../../../tracking-ids';

type SearchFilterProps = {
  debounceDelay?: number;
  minSearchLength?: number;
  onChange?: (value: string, debounced?: boolean) => void;
  trackingId?: string;
  value?: string;
  placeholder?: string;
  expandTriggerTrackingId?: string;
  collapseTriggerTrackingId?: string;
  wrapperStyles?: SerializedStyles;
  expandableMode?: {
    isExpanded: boolean;
    onExpandStateChange: (isExpanded: boolean) => void;
  };
};

const cleanSearchQuery = (query: string): string => {
  if (!query) return '';

  if (validateUUIDV4(query)) {
    return query;
  }

  let cleanedQuery = query
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9 ]/gi, ''); // Remove special characters, keep letters, numbers, spaces

  if (/^\d[\d\s]*$/.test(cleanedQuery)) {
    // Digits only (phone number) → remove ALL spaces
    cleanedQuery = cleanedQuery.replace(/\s+/g, '');
  } else {
    // Contains letters (name) → collapse multiple spaces to single space
    cleanedQuery = cleanedQuery.replace(/\s+/g, ' ');
  }

  return cleanedQuery;
};

export const SearchFilter: React.FC<SearchFilterProps> = ({
  debounceDelay = 1000,
  minSearchLength = 3,
  onChange: handleSearchOnChange,
  trackingId = 'call-intel-search',
  value = '',
  placeholder,
  expandableMode,
  expandTriggerTrackingId,
  collapseTriggerTrackingId,
  wrapperStyles,
  ...rest
}) => {
  const { t } = useTranslation('call-intelligence');

  const getMinLengthError = useCallback(
    (inputValue: string) => {
      const cleanedQuery = cleanSearchQuery(inputValue);

      return cleanedQuery.length > 0 && cleanedQuery.length < minSearchLength
        ? t('Search for at least {{count}} characters', { count: minSearchLength })
        : '';
    },
    [minSearchLength, t]
  );

  const validator = useCallback(
    ({ value: newValue }: { value: string }) => getMinLengthError(newValue),
    [getMinLengthError]
  );

  const handleFieldChange = useCallback(
    (nextValue: string) => {
      handleSearchOnChange?.(nextValue);
      if (nextValue === '') {
        handleSearchOnChange?.('', true);
      }
    },
    [handleSearchOnChange]
  );

  const searchField = useControlledField({
    type: 'text',
    onChange: handleFieldChange,
    value,
    validator,
    validateOnChange: true,
  });

  const debouncedSearchValue = useDebouncedValue(searchField.value, debounceDelay);

  useEffect(() => {
    const rawValue = debouncedSearchValue ?? '';

    if (rawValue !== value) {
      return;
    }

    const cleanedValue = cleanSearchQuery(rawValue);

    if (!rawValue.trim()) {
      return;
    }

    if (cleanedValue.length < minSearchLength) {
      handleSearchOnChange?.(rawValue, true);
      return;
    }

    handleSearchOnChange?.(rawValue, true);

    pendo.track(`${trackingId}:${trackingIds.callIntel.searchQuerySuffix}`, {
      query: cleanedValue,
      searchText: rawValue,
    });
  }, [debouncedSearchValue, handleSearchOnChange, minSearchLength, trackingId, value]);

  return (
    <div css={[styles.container, wrapperStyles]} role='search' aria-label={t('Search bar')}>
      {(!expandableMode || expandableMode.isExpanded) && (
        <SearchField
          css={styles.searchField}
          data-trackingid={trackingId}
          name='search'
          placeholder={placeholder ?? t('Search')}
          {...searchField}
          hideErrorText
          {...rest}
        />
      )}
      {!!expandableMode &&
        (expandableMode.isExpanded ? (
          <Button
            variant='secondary'
            size='large'
            trackingId={collapseTriggerTrackingId}
            onClick={() => expandableMode.onExpandStateChange(false)}
          >
            {t('Done')}
          </Button>
        ) : (
          <Button
            variant='secondary'
            iconName='search'
            size='large'
            css={styles.searchIcon}
            trackingId={expandTriggerTrackingId}
            onClick={() => expandableMode.onExpandStateChange(true)}
            aria-label={t('Open Search')}
          />
        ))}
      {!!searchField.error && (
        <Text as='span' size='small' color='error' css={styles.errorMessage}>
          {searchField.error}
        </Text>
      )}
    </div>
  );
};

const styles = {
  container: css`
    display: flex;
    gap: ${theme.spacing(1)};
    min-height: 40px;
    position: relative;

    & > div:first-child {
      flex: 1;
      display: flex;
      flex-direction: column;
    }
  `,
  searchField: css`
    flex: 1;
    > input {
      @container (max-width: ${breakpoints.xsmall.max}px) {
        font-size: ${theme.font.size.medium};
      }
    }
  `,
  errorMessage: css`
    position: absolute;
    bottom: -20px;
    left: 0;
  `,
  searchIcon: css`
    border: 1px solid ${theme.colors.neutral50};
  `,
};
